import React, { useState } from 'react';
import { appToast } from '../../../../components/AppToast';
import api from '../../../../api/client';

export default function JournalVoucherModal({ isOpen, onClose, month = 'Jul', year = '2026', totalAmount = 0 }) {
  const [voucherNo, setVoucherNo] = useState(`JV-SAL-${month.toUpperCase()}-${year}`);
  const [postingDate, setPostingDate] = useState('2026-07-31');
  const [remarks, setRemarks] = useState(`Salary journal entry for the month of ${month} ${year}`);
  const [isPosting, setIsPosting] = useState(false);

  if (!isOpen) return null;

  const handlePost = async () => {
    setIsPosting(true);
    try {
      const payload = {
        vType: 3, // Journal Voucher
        vNo: voucherNo,
        vDate: postingDate,
        amount: Number(totalAmount) > 0 ? Number(totalAmount) : 514000.0,
        narration: remarks,
        status: 'Cleared',
      };
      const res = await api.post('/accounting/vouchers', payload);
      const returnedVNo = res?.data?.vNo || voucherNo;
      appToast.success(`Salary Journal Voucher #${returnedVNo} posted to General Ledger!`);
      onClose();
    } catch (err) {
      console.error(err);
      const msg = err?.response?.data?.detail || err?.message || 'Failed to post Journal Voucher.';
      appToast.error(msg);
    } finally {
      setIsPosting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-xs p-4 animate-in fade-in duration-150">
      <div className="w-full max-w-xl rounded-2xl border border-outline-variant/30 bg-surface-container-lowest p-6 shadow-2xl space-y-6">
        
        {/* Modal Header */}
        <div className="flex items-center justify-between border-b border-outline-variant/20 pb-4">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-xl bg-primary/10 text-primary flex items-center justify-center">
              <span className="material-symbols-outlined text-2xl">currency_rupee</span>
            </div>
            <div>
              <h3 className="text-base font-bold text-on-surface">
                Payroll Journal Voucher
              </h3>
              <p className="text-xs text-on-surface-variant font-medium">
                Post salary expense and payable provisions into accounts.
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="h-8 w-8 rounded-lg text-on-surface-variant hover:bg-surface-container-high hover:text-on-surface flex items-center justify-center transition cursor-pointer"
          >
            <span className="material-symbols-outlined text-lg">close</span>
          </button>
        </div>

        {/* Modal Form */}
        <div className="space-y-4 text-xs">
          <div className="grid grid-cols-2 gap-4">
            <label className="grid gap-1.5 font-bold text-on-surface-variant">
              <span>Voucher Number</span>
              <input
                type="text"
                value={voucherNo}
                onChange={(e) => setVoucherNo(e.target.value)}
                className="h-9 rounded-xl border border-outline-variant/30 bg-surface-container-lowest px-3 text-xs text-on-surface outline-none focus:ring-2 focus:ring-primary/20"
              />
            </label>

            <label className="grid gap-1.5 font-bold text-on-surface-variant">
              <span>Posting Date</span>
              <input
                type="date"
                value={postingDate}
                onChange={(e) => setPostingDate(e.target.value)}
                className="h-9 rounded-xl border border-outline-variant/30 bg-surface-container-lowest px-3 text-xs text-on-surface outline-none focus:ring-2 focus:ring-primary/20"
              />
            </label>
          </div>

          {/* Ledger Breakdown preview */}
          <div className="rounded-xl border border-outline-variant/20 bg-surface-container-low p-4 space-y-2.5">
            <div className="flex justify-between items-center text-xs font-bold text-on-surface border-b border-outline-variant/20 pb-2">
              <span>Account Head</span>
              <div className="flex gap-8">
                <span>Debit (₹)</span>
                <span>Credit (₹)</span>
              </div>
            </div>

            <div className="flex justify-between items-center text-xs">
              <span className="font-semibold text-on-surface">Dr. Salary & Wages Expense</span>
              <div className="flex gap-10 font-bold text-emerald-700">
                <span>5,14,000.00</span>
                <span className="text-on-surface-variant/40">—</span>
              </div>
            </div>

            <div className="flex justify-between items-center text-xs">
              <span className="font-semibold text-on-surface">Cr. PF Payable</span>
              <div className="flex gap-10 font-bold text-rose-600">
                <span className="text-on-surface-variant/40">—</span>
                <span>28,200.00</span>
              </div>
            </div>

            <div className="flex justify-between items-center text-xs">
              <span className="font-semibold text-on-surface">Cr. TDS / Tax Payable</span>
              <div className="flex gap-10 font-bold text-rose-600">
                <span className="text-on-surface-variant/40">—</span>
                <span>16,300.00</span>
              </div>
            </div>

            <div className="flex justify-between items-center text-xs border-t border-outline-variant/20 pt-2">
              <span className="font-semibold text-on-surface">Cr. Salaries Payable Account</span>
              <div className="flex gap-10 font-extrabold text-on-surface">
                <span className="text-on-surface-variant/40">—</span>
                <span>4,69,500.00</span>
              </div>
            </div>
          </div>

          <label className="grid gap-1.5 font-bold text-on-surface-variant">
            <span>Narration / Remarks</span>
            <textarea
              rows={2}
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
              className="rounded-xl border border-outline-variant/30 bg-surface-container-lowest p-3 text-xs text-on-surface outline-none focus:ring-2 focus:ring-primary/20"
            />
          </label>
        </div>

        {/* Modal Footer */}
        <div className="flex items-center justify-end gap-3 border-t border-outline-variant/20 pt-4">
          <button
            type="button"
            onClick={onClose}
            className="px-5 py-2.5 rounded-xl bg-surface-container-high hover:bg-surface-container-highest text-on-surface text-xs font-bold transition cursor-pointer"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handlePost}
            disabled={isPosting}
            className="px-6 py-2.5 rounded-xl bg-primary hover:bg-primary/90 text-on-primary text-xs font-bold shadow-sm transition disabled:opacity-50 flex items-center gap-2 cursor-pointer"
          >
            {isPosting ? (
              <>
                <span className="material-symbols-outlined text-sm animate-spin">progress_activity</span>
                <span>Posting...</span>
              </>
            ) : (
              <>
                <span className="material-symbols-outlined text-sm">check_circle</span>
                <span>Post Journal Voucher</span>
              </>
            )}
          </button>
        </div>

      </div>
    </div>
  );
}
